<template>
  <div class="gallery">
    <!-- Головне зображення (фікс. пропорція → без CLS) -->
    <div
      class="stage border rounded-2xl overflow-hidden mb-3 bg-gray-100 grid place-items-center text-gray-400"
      tabindex="0"
      aria-label="Галерея товару"
    >
      <img
        v-if="current"
        :src="toSrc(current, LARGEST_GALLERY_WIDTH, GALLERY_QUALITY)"
        :srcset="toSrcset(current, GALLERY_WIDTHS, GALLERY_QUALITY)"
        :sizes="GALLERY_SIZES"
        :alt="`Фото ${currentIndex + 1}`"
        width="800"
        height="1000"
        :loading="currentIndex === 0 ? 'eager' : 'lazy'"
        :decoding="currentIndex === 0 ? 'sync' : 'async'"
        :fetchpriority="currentIndex === 0 ? 'high' : 'auto'"
        :class="[
          'w-full h-full object-cover transition-opacity duration-200',
          stageLoading ? 'opacity-40' : 'opacity-100',
        ]"
        @load="stageLoading = false"
        @error="onStageError"
      />
      <div
        v-else
        class="w-full h-full grid place-items-center"
        aria-hidden="true"
      >
        <ImageIcon :size="40" />
      </div>
    </div>

    <!-- Превʼю -->
    <div class="grid grid-cols-5 gap-2">
      <button
        v-for="(img, i) in images"
        :key="img + i"
        type="button"
        class="thumb border rounded-lg overflow-hidden aspect-square focus:outline-none focus:ring-2 focus:ring-pink-300"
        :class="
          i === currentIndex
            ? 'border'
            : 'border-gray-300 hover:border-black/50'
        "
        :aria-current="i === currentIndex ? 'true' : 'false'"
        :aria-label="`Перемкнути фото ${i + 1}`"
        :title="`Фото ${i + 1}`"
        @click="currentIndex = i"
      >
        <img
          :src="toSrc(img, THUMB_WIDTHS[0])"
          alt=""
          class="w-full h-full object-cover"
          width="120"
          height="120"
          loading="lazy"
          decoding="async"
          @error="onError"
        />
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { ImageIcon } from "lucide-vue-next";
import {
  GALLERY_WIDTHS,
  GALLERY_SIZES,
  GALLERY_QUALITY,
  THUMB_WIDTHS,
} from "~/utils/imageSizes";
import { useImageOptimizer } from "~/composables/useImageOptimizer";

const props = defineProps({
  images: { type: Array, default: () => [] },
  initialIndex: { type: Number, default: 0 },
});
const emit = defineEmits(["change"]);

// Оптимізація кадрів (п.9г): до неї галерея тягнула сирі
// повнорозмірні файли — і в стейдж 800×1000, і в прев'ю 120×120.
const { toSrc, toSrcset, onError } = useImageOptimizer();
const LARGEST_GALLERY_WIDTH = GALLERY_WIDTHS[GALLERY_WIDTHS.length - 1];

/**
 * Індикація завантаження головного кадру.
 *
 * Без неї перемикання кольору виглядає як зависання: браузер тримає
 * ПОПЕРЕДНЮ картинку, доки не декодує нову, і секунда на холодному
 * промаху оптимізатора читається як «клік не спрацював». Прозорість
 * замість спінера — щоб не було стрибка розмітки, і щоб було видно,
 * що кадр саме міняється.
 *
 * Прапорець зводиться в false також по `@error` — інакше після збою
 * оптимізатора (коли показуємо оригінал) кадр лишився б напівпрозорим
 * назавжди.
 */
const stageLoading = ref(false);

function onStageError() {
  stageLoading.value = false;
  onError();
}

const currentIndex = ref(0);

// ініціалізація та ресет при зміні варіанта
watch(
  () => props.images,
  () => {
    currentIndex.value = Math.min(
      props.initialIndex,
      Math.max(props.images.length - 1, 0),
    );
  },
  { immediate: true },
);

// повідомляємо батьку (якщо треба)
watch(currentIndex, (i) => emit("change", i));

const current = computed(() => props.images?.[currentIndex.value] || "");

// Кадр змінився — чекаємо на завантаження нового. Прогрітий кадр
// віддається з кешу і `@load` прилітає в тому ж тіку, тож блимання
// на прогрітих лотах не видно.
watch(
  () => current.value,
  (url) => {
    stageLoading.value = !!url;
  },
);
</script>

<style scoped>
.gallery {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
/* стабільна висота під 4:5 (типові товарні фото) → мінус CLS */
.stage {
  aspect-ratio: 3 / 4;
}
.thumb {
  transition: border-color 0.15s ease;
}
</style>
